/**
 * Notification Storage - IndexedDB persistence for notifications
 *
 * Notifications are local-only (not synced over P2P).
 */

import { openDB, type IDBPDatabase } from 'idb';

const DB_NAME = 'aos-notifications';
const DB_VERSION = 1;
const STORE_NAME = 'notifications';

/**
 * Notification types
 */
export type NotificationType =
  | 'friend_request'  // Someone added you as a friend
  | 'reply'           // Someone replied to your post
  | 'mention'         // Someone mentioned you (future)
  | 'vote'            // Someone voted on your post (optional)
  | 'system';         // System notification

/**
 * Notification structure
 */
export interface Notification {
  /** Unique ID */
  id: string;
  /** Notification type */
  type: NotificationType;
  /** Timestamp when notification was created */
  timestamp: number;
  /** Whether the notification has been read */
  read: boolean;
  /** Title/summary */
  title: string;
  /** Detailed message */
  message?: string;
  /** Public key of the user who triggered the notification */
  fromPubkey?: string;
  /** Related object hash (article, comment, etc.) */
  relatedHash?: string;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Input for creating a notification (without id and timestamp)
 */
export type CreateNotificationInput = Omit<Notification, 'id' | 'timestamp' | 'read'>;

let dbPromise: Promise<IDBPDatabase> | null = null;

/**
 * Get or create the IndexedDB database
 */
async function getDB(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp');
          store.createIndex('read', 'read');
          store.createIndex('type', 'type');
        }
      },
    });
  }
  return dbPromise;
}

/**
 * Generate a unique notification ID
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Create a new notification
 */
export async function createNotification(input: CreateNotificationInput): Promise<Notification> {
  const db = await getDB();
  const notification: Notification = {
    ...input,
    id: generateId(),
    timestamp: Date.now(),
    read: false,
  };
  await db.put(STORE_NAME, notification);
  return notification;
}

/**
 * Get all notifications, sorted by timestamp (newest first)
 */
export async function getAllNotifications(): Promise<Notification[]> {
  const db = await getDB();
  const all = await db.getAll(STORE_NAME);
  return all.sort((a, b) => b.timestamp - a.timestamp);
}

/**
 * Get unread notifications
 */
export async function getUnreadNotifications(): Promise<Notification[]> {
  const db = await getDB();
  const all = await db.getAll(STORE_NAME);
  return all.filter(n => !n.read).sort((a, b) => b.timestamp - a.timestamp);
}

/**
 * Get unread count
 */
export async function getUnreadCount(): Promise<number> {
  const db = await getDB();
  const all = await db.getAll(STORE_NAME);
  return all.filter(n => !n.read).length;
}

/**
 * Mark a notification as read
 */
export async function markAsRead(id: string): Promise<void> {
  const db = await getDB();
  const notification = await db.get(STORE_NAME, id);
  if (notification) {
    notification.read = true;
    await db.put(STORE_NAME, notification);
  }
}

/**
 * Mark all notifications as read
 */
export async function markAllAsRead(): Promise<void> {
  const db = await getDB();
  const all = await db.getAll(STORE_NAME);
  const tx = db.transaction(STORE_NAME, 'readwrite');
  for (const notification of all) {
    if (!notification.read) {
      notification.read = true;
      await tx.store.put(notification);
    }
  }
  await tx.done;
}

/**
 * Delete a notification
 */
export async function deleteNotification(id: string): Promise<void> {
  const db = await getDB();
  await db.delete(STORE_NAME, id);
}

/**
 * Delete all notifications
 */
export async function clearAllNotifications(): Promise<void> {
  const db = await getDB();
  await db.clear(STORE_NAME);
}

/**
 * Check if a notification already exists (to avoid duplicates)
 * Matches by type, fromPubkey, and relatedHash
 */
export async function notificationExists(
  type: NotificationType,
  fromPubkey?: string,
  relatedHash?: string
): Promise<boolean> {
  const db = await getDB();
  const all = await db.getAllFromIndex(STORE_NAME, 'type', type);
  return all.some(n =>
    n.fromPubkey === fromPubkey && n.relatedHash === relatedHash
  );
}
